import React, { useEffect, useMemo, useState } from 'react';
import styles from './ProductDashboardCharts.module.scss';
import { MenuProps } from 'antd';
import {
  productCarbonFootprintOptions,
  eachProductAnnualOptions,
  productTotalCarbonForEachCycle,
  productBreakdownTotalCarbonForEachCycle,
} from './ProductDashboardChartsConstants';
import ProductsService from 'services/ProductsService/products.service';
import { useParams } from 'react-router-dom';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import {
  ProductStagesEnums,
  ProductStagesValuesEnums,
} from 'enums/ProductStagesEnums';
import { SortKeys } from 'enums/GraphDropdownEnums';
import { DataMappingProps } from 'enums/CreateProductStageNavigationBarEnums';
import { BreakDownDropdownOptionsEnums } from 'enums/BreakDownDropdownOptions';
import { COProductCardPropsEnums } from 'enums/COProductCardEnums';
import Header from './header';
import CarbonFootprintSection from './CarbonFootprintSection/CarbonFootprintSection';
import DqrMetricComponent from './DqrMetricComponent/DqrMetricComponent';
import ProductGraphs from './ProductGraphs';
import {
  calculateCarbonValueOfEachStage,
  downloadProductEmissionCSV,
  downloadProductBreakdownEmissionCSV,
  getCurrentStageName,
  convertToRawMaterialOptions,
  convertToProcessingFacility,
  convertToDistributionAndStorage,
  handleChangeCard,
  handleProductAnnualOptions,
  setRotation,
} from './productGraphUtils';
import EmissionsService from 'services/ParentCompanySetup/emissions.service';
import DashboardService from 'services/DashboardService/dashboard.service';
import ProductCreationService from 'services/ProductsService/productCreation.service';
import EmissionService from 'services/ProductsService/emission.service';
export interface BreakdownDrowdownTypes {
  type: string;
  stage: string;
}

const ProductDashboardCharts: React.FC = () => {
  const {
    fetchEmissionEachStage,
    emissionEachStage,
    emissionEachStageLoading,
    downloadCSVForEachLifeCycle,
    downloadbreakdownGraphCSV,
  } = ProductsService();
  const {
    fetchProductEmissionsStage1,
    fetchProductEmissionsStage2,
    fetchProductEmissionsStage3,
    fetchProductEmissionsStage4,
    stage1Emissions,
    stage2Emissions,
    stage3Emissions,
    stage4Emissions,
    fetchTotalEmissionAnnualProduction,
    totalEmissionAnnualProduction,
  } = EmissionService();
  const { fetchSingleProduct, newProduct } = ProductCreationService();
  const {
    fetchGoalProduct,
    goalProduct,
    setGoal,
    updateGoal,
    dqrData,
    fetchDqr,
  } = DashboardService();
  const { yoyProductData, fetchYoYProductEmissions } = EmissionsService();

  const [businessModelValue, setBusinessModelValue] =
    useState<ProductStagesValuesEnums>(ProductStagesValuesEnums.B2B);
  const [productEmissionForLifeCycleSort, setProductEmissionForLifeCycleSort] =
    useState<string>(SortKeys.DEFAULT);
  const [
    productBreakdownEmissionForLifeCycleSort,
    setProducBreakdownEmissionForLifeCycleSort,
  ] = useState<string>(SortKeys.DEFAULT);
  const [selectedType, setSelectedType] = useState<boolean>(true);
  const [editModalProductState, setEditModaProductState] =
    useState<boolean>(false);
  const [selectedBusinessModelValue, setSelectedBusinessModelValue] =
    useState<string>(ProductStagesValuesEnums.B2B);
  const [selectedStage, setSelectedStage] = useState<BreakdownDrowdownTypes>({
    type: BreakDownDropdownOptionsEnums.TOTAL,
    stage: DataMappingProps.STAGE_1,
  });
  const handleSubmit = () => {};
  const initialValues = {};
  const { id } = useParams();
  const productId = Number(id);

  const { degree, translateX, translateY } = useMemo(
    () => setRotation(dqrData.totalScore),
    [dqrData.totalScore]
  );

  const breakdownData = useMemo(() => {
    let data;
    switch (selectedStage?.stage) {
      case DataMappingProps.STAGE_1:
        data =
          stage1Emissions &&
          convertToRawMaterialOptions(stage1Emissions, selectedStage);
        break;
      case DataMappingProps.STAGE_2:
        data =
          stage2Emissions &&
          convertToRawMaterialOptions(stage2Emissions, selectedStage);
        break;
      case DataMappingProps.STAGE_3:
        data =
          stage3Emissions &&
          convertToProcessingFacility(stage3Emissions, selectedStage);
        break;
      case DataMappingProps.STAGE_4:
        data =
          stage4Emissions &&
          convertToDistributionAndStorage(stage4Emissions, selectedStage);
        break;
      default:
        break;
    }

    return data?.sort(
      (
        a: ProductRawMaterialTransportations,
        b: ProductRawMaterialTransportations
      ) => {
        if (productBreakdownEmissionForLifeCycleSort === SortKeys.DEFAULT) {
          return 0; // No sorting for the default case
        } else if (
          productBreakdownEmissionForLifeCycleSort ===
          SortKeys.VALUE_LOW_TO_HIGH
        ) {
          return Number(a.emission) - Number(b.emission);
        } else {
          return Number(b.emission) - Number(a.emission);
        }
      }
    );
  }, [
    selectedStage,
    stage1Emissions,
    stage2Emissions,
    stage3Emissions,
    stage4Emissions,
    productBreakdownEmissionForLifeCycleSort,
  ]);

  const handleEditProductModal = () => setEditModaProductState(false);
  const handleOpenProductModal = () => setEditModaProductState(true);

  useEffect(() => {
    fetchEmissionEachStage(productId, 'B2B', selectedType);
    fetchProductEmissionsStage1(productId.toString());
    fetchProductEmissionsStage2(productId.toString());
    fetchProductEmissionsStage3(productId.toString());
    fetchProductEmissionsStage4(productId.toString());
    fetchGoalProduct({ type: 'product', dashboard_id: productId });
    fetchSingleProduct(Number(productId));
    fetchYoYProductEmissions({ productId });
  }, []);
  useEffect(() => {
    if (
      productId &&
      newProduct &&
      newProduct?.status === COProductCardPropsEnums.APPROVED
    ) {
      fetchDqr(productId);
    }
  }, [productId, newProduct]);

  useEffect(() => {
    fetchTotalEmissionAnnualProduction(productId, {
      business_model: businessModelValue,
    });
  }, [businessModelValue]);

  const carbonValueOfEachStage = useMemo(
    () =>
      calculateCarbonValueOfEachStage(
        emissionEachStage,
        selectedType,
        productEmissionForLifeCycleSort
      ),
    [emissionEachStage, selectedType, productEmissionForLifeCycleSort]
  );

  const handleCardChange = (values: ProductStagesEnums) => {
    handleChangeCard(values, setBusinessModelValue);
  };

  const handleAnnualOptions = (value: string) => {
    handleProductAnnualOptions(value, setSelectedStage);
  };

  const handleSortProductForLifeCycle: MenuProps['onClick'] = ({ key }) => {
    setProductEmissionForLifeCycleSort(key);
  };

  const handleSortBreakDownProductForLifeCycle: MenuProps['onClick'] = ({
    key,
  }) => {
    setProducBreakdownEmissionForLifeCycleSort(key);
  };

  const handleDownloadProductEmissionCSV = async () => {
    await downloadProductEmissionCSV(
      Number(id),
      selectedBusinessModelValue,
      productEmissionForLifeCycleSort,
      downloadCSVForEachLifeCycle
    );
  };

  const handleDownloadProductBreakdownEmissionCSV = () => {
    downloadProductBreakdownEmissionCSV(
      selectedStage,
      productId,
      productBreakdownEmissionForLifeCycleSort,
      downloadbreakdownGraphCSV
    );
  };

  return (
    <DashboardContainer>
      <div className={styles.contentContainer}>
        <Header newProduct={newProduct} />
        <ProductGraphs
          productId={productId}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          selectedBusinessModelValue={selectedBusinessModelValue}
          setSelectedBusinessModelValue={setSelectedBusinessModelValue}
          productTotalCarbonForEachCycle={productTotalCarbonForEachCycle}
          productCarbonFootprintOptions={productCarbonFootprintOptions}
          carbonValueOfEachStage={carbonValueOfEachStage}
          emissionEachStageLoading={emissionEachStageLoading}
          handleSortProductForLifeCycle={handleSortProductForLifeCycle}
          downloadProductEmissionCSV={handleDownloadProductEmissionCSV}
          yoyProductData={yoyProductData}
          fetchYoYProductEmissions={fetchYoYProductEmissions}
          newProduct={newProduct}
          productBreakdownTotalCarbonForEachCycle={
            productBreakdownTotalCarbonForEachCycle
          }
          selectedStage={selectedStage}
          setSelectedStage={setSelectedStage}
          eachProductAnnualOptions={eachProductAnnualOptions}
          handleProductAnnualOptions={handleAnnualOptions}
          breakdownData={breakdownData}
          handleSortBreakDownProductForLifeCycle={
            handleSortBreakDownProductForLifeCycle
          }
          downloadProductBreakdownEmissionCSV={
            handleDownloadProductBreakdownEmissionCSV
          }
          productEmissionForLifeCycleSort={productEmissionForLifeCycleSort}
          productBreakdownEmissionForLifeCycleSort={
            productBreakdownEmissionForLifeCycleSort
          }
          getCurrentStageName={() =>
            getCurrentStageName(eachProductAnnualOptions, selectedStage)
          }
          handleSubmit={handleSubmit}
          initialValues={initialValues}
          fetchEmissionEachStage={fetchEmissionEachStage}
        />
        {newProduct?.status === COProductCardPropsEnums.APPROVED &&
          dqrData.dqrMetrics !== null && (
            <DqrMetricComponent
              dqrData={dqrData}
              degree={degree}
              translateX={translateX}
              translateY={translateY}
            />
          )}
        <CarbonFootprintSection
          productCarbonFootprintOptions={productCarbonFootprintOptions}
          handleChangeCard={handleCardChange}
          editModalProductState={editModalProductState}
          handleEditProductModal={handleEditProductModal}
          goalProduct={goalProduct}
          setGoal={setGoal}
          updateGoal={updateGoal}
          productId={productId}
          totalEmissionAnnualProduction={Number(totalEmissionAnnualProduction)}
          handleOpenProductModal={handleOpenProductModal}
          newProduct={newProduct}
        />
      </div>
    </DashboardContainer>
  );
};

export default ProductDashboardCharts;
